import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  RefreshCw,
  Upload,
  XCircle,
  CheckCircle,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  Monitor,
  Smartphone,
} from "react-feather";
import { useGet, usePost, usePatch, useGetLazy } from "../../api";
import {
  IAutoCouponTemplate,
  IAutoCouponTemplateListResponse,
  IAutoCouponSyncSqlResponse,
  IAutoCouponTemplateCompareResponse,
  IAutoCouponLiveTemplate,
  IAutoCouponLiveTemplateListResponse,
  IAutoCouponSyncAuditListResponse,
  IAutoCouponTemplateStats,
  IAutoCouponTemplateStatsFigures,
  IAutoCouponUnsyncedListResponse,
  IAutoCouponUnsyncedTemplate,
  IAutoCouponBulkPutLiveResponse,
  IAutoCouponSqlDiffCheckResponse,
  AUTO_COUPON_BULK_PUT_LIVE_MAX,
  TAutoCouponPutLiveMode,
  TSyncFieldChoice,
  TAutoCouponToolTestStatus,
} from "../../interfaces";

/** RFC-4180-ish CSV cell escaping. */
const csvEscape = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/**
 * Compact, human-readable relative time (e.g. "3 hours ago", "yesterday").
 * A month or older falls back to the plain date, which reads better than
 * "last month" / "3 months ago".
 */
const timeAgo = (iso: string): string => {
  const d = new Date(iso);
  const deltaSec = (d.getTime() - Date.now()) / 1000; // negative = past
  const absSec = Math.abs(deltaSec);
  const DAY = 60 * 60 * 24;
  if (absSec >= DAY * 30) return d.toLocaleDateString();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", DAY],
    ["hour", 60 * 60],
    ["minute", 60],
    ["second", 1],
  ];
  for (const [unit, secs] of units) {
    if (absSec >= secs || unit === "second") {
      return rtf.format(Math.round(deltaSec / secs), unit);
    }
  }
  return "";
};

/** Compact device icon(s) for a template's view (desktop / mobile / both). */
const TemplateViewIcon: React.FC<{ view: string }> = ({ view }) => {
  const showDesktop = view === "desktop" || view === "both";
  const showMobile = view === "mobile" || view === "both";
  return (
    <span
      className="d-inline-flex align-items-center gap-1 text-secondary"
      title={view}
    >
      {showDesktop && <Monitor size={15} />}
      {showMobile && <Smartphone size={15} />}
    </span>
  );
};

/** Valid / invalid status icon shown next to a template's domain. */
const TemplateValidIcon: React.FC<{ invalid: boolean }> = ({ invalid }) => (
  <span
    className="d-inline-flex align-items-center flex-shrink-0"
    title={invalid ? "Invalid" : "Valid"}
  >
    {invalid ? (
      <XCircle size={15} className="text-danger" />
    ) : (
      <CheckCircle size={15} className="text-success" />
    )}
  </span>
);

// ─── Stats cards ───────────────────────────────────────────────────────────

const StatCard: React.FC<{
  title: string;
  subtitle: string;
  figures: IAutoCouponTemplateStatsFigures | null;
  /** Optional second figure set shown alongside, e.g. "12 created · 10 valid". */
  secondary?: {
    label: string;
    figures: IAutoCouponTemplateStatsFigures;
  } | null;
  /** Optional labeled sub-sections, e.g. new vs updates breakdown. */
  sections?: Array<{
    label: string;
    figures: IAutoCouponTemplateStatsFigures | null;
  }> | null;
  loading: boolean;
}> = ({ title, subtitle, figures, secondary, sections, loading }) => (
  <Card className="h-100">
    <Card.Body className="py-2 px-3">
      <div className="small text-muted">{title}</div>
      {sections ? (
        sections.map((s) => (
          <div key={s.label} className="mt-1">
            <div className="d-flex align-items-baseline gap-2 flex-wrap">
              <span className="small text-muted">{s.label}:</span>
              <h3 className="mb-0">
                {s.figures ? (
                  s.figures.combined
                ) : loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "—"
                )}
              </h3>
              {s.figures && (
                <span className="small text-muted">
                  Auto <strong>{s.figures.auto}</strong> · SKU{" "}
                  <strong>{s.figures.sku}</strong> · Both{" "}
                  <strong>{s.figures.both}</strong>
                </span>
              )}
            </div>
          </div>
        ))
      ) : (
        <>
          <div className="d-flex align-items-baseline gap-2 flex-wrap">
            <h3 className="mb-0">
              {figures ? (
                figures.combined
              ) : loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "—"
              )}
              {figures && secondary && (
                <span className="fs-5 text-muted">
                  {" "}
                  created · {secondary.figures.combined} {secondary.label}
                </span>
              )}
            </h3>
            {figures && !secondary && (
              <span className="small text-muted">
                Auto <strong>{figures.auto}</strong> · SKU{" "}
                <strong>{figures.sku}</strong> · Both{" "}
                <strong>{figures.both}</strong>
              </span>
            )}
          </div>
          {figures && secondary && (
            <div className="small text-muted">
              Auto <strong>{figures.auto}</strong> ({secondary.figures.auto}{" "}
              {secondary.label}) · SKU <strong>{figures.sku}</strong> (
              {secondary.figures.sku} {secondary.label})
            </div>
          )}
        </>
      )}
      <div className="small text-muted">{subtitle}</div>
    </Card.Body>
  </Card>
);

const TemplateStatsCards: React.FC = () => {
  const { data, loading, error, execute } =
    useGetLazy<IAutoCouponTemplateStats>();

  React.useEffect(() => {
    execute("/auto-coupons/templates/stats").catch(() => {
      // handled via the hook's error state
    });
  }, [execute]);

  const handleRefresh = () => {
    execute("/auto-coupons/templates/stats?refresh=true").catch(() => {
      // handled via the hook's error state
    });
  };

  const { execute: fetchCsv } = useGetLazy<string>();
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Downloads one row per live domain with a flag for each half (auto / SKU),
  // i.e. the domains behind the "Live in SQL" card.
  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const csv = await fetchCsv(
        "/auto-coupons/templates/live-domains-export",
        { responseType: "text" }
      );
      if (csv == null) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `live-template-domains-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // best-effort download; the stats hook already surfaces load errors
    } finally {
      setDownloadingCsv(false);
    }
  };

  const cards: {
    title: string;
    subtitle: string;
    figures: IAutoCouponTemplateStatsFigures | null;
    secondary?: {
      label: string;
      figures: IAutoCouponTemplateStatsFigures;
    } | null;
    sections?: Array<{
      label: string;
      figures: IAutoCouponTemplateStatsFigures | null;
    }> | null;
  }[] = [
    {
      title: "Live in SQL",
      subtitle: "Active templates, unique domains",
      figures: data?.live ?? null,
    },
    {
      title: "Synced via tool",
      subtitle: "Domains pushed live from here",
      figures: data?.synced ?? null,
    },
    {
      title: "Ready to go live",
      subtitle: "Valid staging, not yet synced",
      figures: data?.outstanding ?? null,
      sections: [
        { label: "new", figures: data?.outstandingNew ?? null },
        { label: "updates", figures: data?.outstandingUpdates ?? null },
      ],
    },
    {
      title: "Created last 24h",
      subtitle: "New staging templates",
      figures: data?.createdLast24h ?? null,
      secondary: data?.createdLast24hValid
        ? { label: "valid", figures: data.createdLast24hValid }
        : null,
    },
  ];

  return (
    <div className="mb-3">
      <Row className="g-2">
        {cards.map((card) => (
          <Col md={3} sm={6} key={card.title}>
            <StatCard {...card} loading={loading} />
          </Col>
        ))}
      </Row>
      <div className="d-flex align-items-center gap-2 mt-1">
        <Button
          variant="link"
          size="sm"
          className="p-0 small text-decoration-none"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw size={12} className="me-1" />
          Refresh stats
        </Button>
        <Button
          variant="link"
          size="sm"
          className="p-0 small text-decoration-none"
          onClick={handleDownloadCsv}
          disabled={downloadingCsv}
        >
          <Download size={12} className="me-1" />
          {downloadingCsv ? "Preparing CSV…" : "Download live domains CSV"}
        </Button>
        {data?.cachedAt && (
          <span className="small text-muted">
            as of {new Date(data.cachedAt).toLocaleTimeString()}
          </span>
        )}
        {error && (
          <span className="small text-danger">Failed to load stats.</span>
        )}
      </div>
    </div>
  );
};

// ─── Selector preview modal ────────────────────────────────────────────────

const SelectorsModal: React.FC<{
  template: IAutoCouponTemplate | null;
  onClose: () => void;
}> = ({ template, onClose }) => (
  <Modal show={!!template} onHide={onClose} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>
        Selectors — {template?.domain} / {template?.country}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {template && (
        <>
          {template.recentUserEmail && (
            <p className="small mb-3">
              <span className="text-muted me-1">Created by:</span>
              {template.recentUserEmail}
            </p>
          )}

          <h6 className="mb-1">Coupon selectors</h6>
          <pre
            className="bg-light p-3 rounded"
            style={{ fontSize: 12, maxHeight: 280, overflow: "auto" }}
          >
            {JSON.stringify(template.selectors, null, 2)}
          </pre>

          <h6 className="mb-1 mt-3">SKU selectors</h6>
          {template.skuSelectors ? (
            <pre
              className="bg-light p-3 rounded"
              style={{ fontSize: 12, maxHeight: 200, overflow: "auto" }}
            >
              {JSON.stringify(template.skuSelectors, null, 2)}
            </pre>
          ) : (
            <p className="text-muted small">No SKU selectors saved.</p>
          )}

          {template.skuSelectors && (
            <>
              <h6 className="mb-1 mt-3">SKU cart-page URL regex</h6>
              {template.skuCheckoutPageUrlRegex ? (
                <code
                  className="d-block bg-light p-2 rounded small"
                  style={{ wordBreak: "break-all" }}
                >
                  {template.skuCheckoutPageUrlRegex}
                </code>
              ) : (
                <p className="text-muted small">Not captured.</p>
              )}
            </>
          )}

          <h6 className="mb-1 mt-3">Timings</h6>
          {template.timings ? (
            <pre className="bg-light p-3 rounded" style={{ fontSize: 12 }}>
              {JSON.stringify(template.timings, null, 2)}
            </pre>
          ) : (
            <p className="text-muted small">No timings saved.</p>
          )}

          <h6 className="mb-1 mt-3">Checkout URL regex</h6>
          {template.checkoutPageUrlRegex ? (
            <code
              className="d-block bg-light p-2 rounded small"
              style={{ wordBreak: "break-all" }}
            >
              {template.checkoutPageUrlRegex}
            </code>
          ) : (
            <p className="text-muted small">Not captured.</p>
          )}
        </>
      )}
    </Modal.Body>
  </Modal>
);

// ─── Load-from-SQL modal ───────────────────────────────────────────────────

const LoadSqlModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onLoaded: (tpl: IAutoCouponTemplate) => void;
}> = ({ show, onClose, onLoaded }) => {
  const [domain, setDomain] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { execute: load } = useGetLazy<IAutoCouponTemplate>();

  const handleLoad = async () => {
    if (!domain || !country) return;
    setLoading(true);
    setError(null);
    try {
      const tpl = await load(
        `/auto-coupons/templates/from-sql?domain=${encodeURIComponent(domain)}&country=${encodeURIComponent(country)}`
      );
      if (tpl) onLoaded(tpl);
    } catch {
      setError("Template not found in SQL for this domain / country.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Load Template from SQL</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Domain</Form.Label>
            <Form.Control
              placeholder="e.g. amazon.co.uk"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Country Code</Form.Label>
            <Form.Control
              placeholder="e.g. GB"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={loading || !domain || !country}
          onClick={handleLoad}
        >
          {loading ? <Spinner size="sm" animation="border" /> : "Load"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Push-live (selective sync) modal ──────────────────────────────────────

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const PushLiveModal: React.FC<{
  templateId: string | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ templateId, onClose, onDone }) => {
  const [compare, setCompare] =
    useState<IAutoCouponTemplateCompareResponse | null>(null);
  const [selections, setSelections] = useState<
    Record<string, TSyncFieldChoice>
  >({});
  const [mode, setMode] = useState<"existing" | "new" | "skip">("existing");
  const [targetTemplateId, setTargetTemplateId] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<number | "">("");
  const [skuMode, setSkuMode] = useState<"existing" | "new" | "skip">("skip");
  const [skuTargetTemplateId, setSkuTargetTemplateId] = useState<string | null>(
    null
  );
  const [skuNewVersion, setSkuNewVersion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IAutoCouponSyncSqlResponse | null>(null);

  const { execute: loadCompare } =
    useGetLazy<IAutoCouponTemplateCompareResponse>();
  const { execute: submitSync } = usePost<IAutoCouponSyncSqlResponse>();

  React.useEffect(() => {
    if (!templateId) {
      setCompare(null);
      setSelections({});
      setMode("existing");
      setTargetTemplateId(null);
      setNewVersion("");
      setSkuMode("skip");
      setSkuTargetTemplateId(null);
      setSkuNewVersion("");
      setError(null);
      setResult(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const params = new URLSearchParams();
        if (targetTemplateId) params.set("targetTemplateId", targetTemplateId);
        if (skuTargetTemplateId)
          params.set("skuTargetTemplateId", skuTargetTemplateId);
        const query = params.toString() ? `?${params.toString()}` : "";
        const data = await loadCompare(
          `/auto-coupons/templates/${templateId}/compare-sql${query}`
        );
        if (cancelled || !data) return;
        setCompare(data);
        setNewVersion(data.mongoVersion ?? "");
        // Default: keep live values, except when the template isn't in SQL yet
        // (then take the working values to seed the live record).
        const defaults: Record<string, TSyncFieldChoice> = {};
        for (const f of data.fields) {
          defaults[f.key] = data.existsInSql ? "keep" : "take";
        }
        for (const f of data.sku.fields) {
          defaults[f.key] = "take";
        }
        setSelections(defaults);
        // SKU panel defaults: push the working SKU when present, defaulting to
        // updating the existing SKU row, else seeding a new one.
        if (!data.sku.hasWorking) {
          setSkuMode("skip");
        } else if (data.sku.existsInSql) {
          setSkuMode("existing");
        } else {
          setSkuMode("new");
        }
        setSkuTargetTemplateId(data.sku.comparedTemplateId);
        setSkuNewVersion(data.sku.comparedVersion ?? "V1");
      } catch {
        if (!cancelled) setError("Failed to compare template with SQL.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId, targetTemplateId, skuTargetTemplateId, loadCompare]);

  const setChoice = (key: string, choice: TSyncFieldChoice) =>
    setSelections((prev) => ({ ...prev, [key]: choice }));

  const setAll = (choice: TSyncFieldChoice) => {
    if (!compare) return;
    const next: Record<string, TSyncFieldChoice> = {};
    for (const f of compare.fields) next[f.key] = choice;
    setSelections(next);
  };

  const switchMode = (next: "existing" | "new" | "skip") => {
    setMode(next);
    // Pushing a new version seeds a fresh row from the working template.
    if (next === "new") setAll("take");
  };

  const selectedTemplateId =
    targetTemplateId ?? compare?.comparedTemplateId ?? "";

  const selectedSkuTemplateId =
    skuTargetTemplateId ?? compare?.sku.comparedTemplateId ?? "";

  const handleSubmit = async () => {
    if (!templateId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitSync(
        `/auto-coupons/templates/${templateId}/sync-sql/selective`,
        {
          selections,
          mode,
          targetTemplateId:
            mode === "existing" ? selectedTemplateId || null : null,
          newVersion:
            mode === "new"
              ? newVersion === ""
                ? null
                : Number(newVersion)
              : null,
          skuMode,
          skuTargetTemplateId:
            skuMode === "existing" ? selectedSkuTemplateId || null : null,
          skuNewVersion: skuMode === "new" ? skuNewVersion || null : null,
        }
      );
      setResult(res);
      if (res.success) onDone();
    } catch (err: any) {
      setError(err?.data?.message ?? "Sync failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const groups = compare
    ? Array.from(new Set(compare.fields.map((f) => f.group)))
    : [];
  const changedCount = compare
    ? compare.fields.filter((f) => f.differs && selections[f.key] === "take")
        .length
    : 0;

  const autoWillWrite = mode !== "skip" && (mode === "new" || changedCount > 0);
  const skuWillWrite = !!compare?.sku.hasWorking && skuMode !== "skip";
  const skuChangedCount = compare
    ? compare.sku.fields.filter(
        (f) => f.differs && selections[f.key] === "take"
      ).length
    : 0;
  const nothingToDo = !autoWillWrite && !skuWillWrite;
  const submitLabel = (() => {
    const parts: string[] = [];
    if (autoWillWrite)
      parts.push(
        mode === "new"
          ? `auto v${newVersion === "" ? "?" : newVersion}`
          : `auto (${changedCount})`
      );
    if (skuWillWrite)
      parts.push(
        skuMode === "new"
          ? `SKU ${skuNewVersion || "V1"}`
          : `SKU (${skuChangedCount})`
      );
    return parts.length ? `Push ${parts.join(" + ")}` : "Nothing to push";
  })();

  return (
    <Modal show={!!templateId} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Push live
          {compare ? ` — ${compare.domain} / ${compare.templateView}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {result && (
          <Alert variant={result.success ? "success" : "danger"}>
            {result.message}
          </Alert>
        )}
        {!loading && compare && (
          <>
            <h6 className="fw-bold text-uppercase small text-muted mb-2">
              Auto-coupon template
            </h6>
            <Card body className="mb-3 bg-light">
              <div className="d-flex gap-4 align-items-start flex-wrap">
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">
                    Push mode
                  </Form.Label>
                  <div className="d-flex flex-column gap-1">
                    <Form.Check
                      type="radio"
                      id="push-mode-existing"
                      name="push-mode"
                      label="Sync with existing version"
                      checked={mode === "existing"}
                      onChange={() => switchMode("existing")}
                    />
                    <Form.Check
                      type="radio"
                      id="push-mode-new"
                      name="push-mode"
                      label="Push as new version"
                      checked={mode === "new"}
                      onChange={() => switchMode("new")}
                    />
                    <Form.Check
                      type="radio"
                      id="push-mode-skip"
                      name="push-mode"
                      label="Skip auto template"
                      checked={mode === "skip"}
                      onChange={() => switchMode("skip")}
                    />
                  </div>
                </Form.Group>
                {mode === "existing" ? (
                  <Form.Group style={{ minWidth: 220 }}>
                    <Form.Label className="small fw-bold mb-1">
                      Target version
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedTemplateId}
                      disabled={compare.availableVersions.length === 0}
                      onChange={(e) =>
                        setTargetTemplateId(e.target.value || null)
                      }
                    >
                      {compare.availableVersions.length === 0 && (
                        <option value="">No live versions yet</option>
                      )}
                      {compare.availableVersions.map((v) => (
                        <option key={v.templateId} value={v.templateId}>
                          v{v.version ?? "?"}
                          {v.version === compare.mongoVersion
                            ? " (current)"
                            : ""}
                          {v.isActive ? " · active" : " · inactive"}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Defaults to the working version (v
                      {compare.mongoVersion ?? "?"}).
                    </Form.Text>
                  </Form.Group>
                ) : mode === "new" ? (
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label className="small fw-bold mb-1">
                      New version number
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={newVersion}
                      onChange={(e) =>
                        setNewVersion(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    />
                    <Form.Text className="text-muted">
                      Defaults to the working version (v
                      {compare.mongoVersion ?? "?"}). Other versions are left
                      untouched.
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <Alert variant="secondary" className="py-2 mb-0">
                    Auto-coupon template will be left unchanged.
                  </Alert>
                )}
              </div>
            </Card>
            {mode === "new" ? (
              <Alert variant="info" className="py-2">
                A brand-new live row will be inserted from the working template.
                Existing versions stay as they are.
              </Alert>
            ) : mode === "existing" ? (
              !compare.existsInSql && (
                <Alert variant="info" className="py-2">
                  No live template exists in SQL for this domain yet — fields
                  default to <strong>Take</strong> to seed a new live record.
                </Alert>
              )
            ) : null}
            {mode !== "skip" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">
                    For each field choose <strong>Take</strong> (use working
                    value) or <strong>Keep</strong> (retain live value).
                  </span>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setAll("take")}
                    >
                      Take all
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setAll("keep")}
                    >
                      Keep all
                    </Button>
                  </div>
                </div>
                {groups.map((group) => (
                  <div key={group} className="mb-3">
                    <h6 className="text-uppercase text-muted small mb-1">
                      {group}
                    </h6>
                    <Table size="sm" bordered className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "26%" }}>Field</th>
                          <th style={{ width: "30%" }}>Live (SQL)</th>
                          <th style={{ width: "30%" }}>Working (staging)</th>
                          <th style={{ width: "14%" }}>Choice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compare.fields
                          .filter((f) => f.group === group)
                          .map((f) => (
                            <tr
                              key={f.key}
                              className={
                                f.differs ? "table-warning" : undefined
                              }
                            >
                              <td className="small">{f.label}</td>
                              <td
                                className="small text-muted"
                                style={{ wordBreak: "break-all" }}
                              >
                                {formatValue(f.sqlValue)}
                              </td>
                              <td
                                className="small"
                                style={{ wordBreak: "break-all" }}
                              >
                                {formatValue(f.mongoValue)}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Form.Check
                                    type="radio"
                                    id={`${f.key}-take`}
                                    label="Take"
                                    name={f.key}
                                    checked={selections[f.key] === "take"}
                                    disabled={!f.differs}
                                    onChange={() => setChoice(f.key, "take")}
                                  />
                                  <Form.Check
                                    type="radio"
                                    id={`${f.key}-keep`}
                                    label="Keep"
                                    name={f.key}
                                    checked={selections[f.key] !== "take"}
                                    disabled={!f.differs}
                                    onChange={() => setChoice(f.key, "keep")}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ))}
              </>
            )}

            <hr className="my-3" />
            <h6 className="fw-bold text-uppercase small text-muted mb-2">
              SKU template
            </h6>
            {!compare.sku.hasWorking ? (
              <Alert variant="secondary" className="py-2 mb-0">
                No working SKU selectors found for{" "}
                <strong>{compare.domain}</strong> — the SKU template will be
                left unchanged.
              </Alert>
            ) : (
              <>
                <Card body className="mb-3 bg-light">
                  <div className="d-flex gap-4 align-items-start flex-wrap">
                    <Form.Group>
                      <Form.Label className="small fw-bold mb-1">
                        SKU push mode
                      </Form.Label>
                      <div className="d-flex flex-column gap-1">
                        <Form.Check
                          type="radio"
                          id="sku-mode-existing"
                          name="sku-push-mode"
                          label="Sync with existing SKU version"
                          checked={skuMode === "existing"}
                          disabled={compare.sku.availableVersions.length === 0}
                          onChange={() => setSkuMode("existing")}
                        />
                        <Form.Check
                          type="radio"
                          id="sku-mode-new"
                          name="sku-push-mode"
                          label="Push as new SKU version"
                          checked={skuMode === "new"}
                          onChange={() => setSkuMode("new")}
                        />
                        <Form.Check
                          type="radio"
                          id="sku-mode-skip"
                          name="sku-push-mode"
                          label="Skip SKU template"
                          checked={skuMode === "skip"}
                          onChange={() => setSkuMode("skip")}
                        />
                      </div>
                    </Form.Group>
                    {skuMode === "existing" && (
                      <Form.Group style={{ minWidth: 220 }}>
                        <Form.Label className="small fw-bold mb-1">
                          Target SKU version
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={selectedSkuTemplateId}
                          disabled={compare.sku.availableVersions.length === 0}
                          onChange={(e) =>
                            setSkuTargetTemplateId(e.target.value || null)
                          }
                        >
                          {compare.sku.availableVersions.length === 0 && (
                            <option value="">No live SKU versions yet</option>
                          )}
                          {compare.sku.availableVersions.map((v) => (
                            <option key={v.templateId} value={v.templateId}>
                              {v.version ?? "(no version)"}
                              {v.isActive ? " · active" : " · inactive"}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                    {skuMode === "new" && (
                      <Form.Group style={{ minWidth: 160 }}>
                        <Form.Label className="small fw-bold mb-1">
                          New SKU version label
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="V1"
                          value={skuNewVersion}
                          onChange={(e) => setSkuNewVersion(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                          A new SKU row is inserted; existing SKU rows are left
                          untouched.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </div>
                </Card>
                {skuMode === "skip" ? (
                  <Alert variant="secondary" className="py-2 mb-0">
                    The SKU template will be left unchanged.
                  </Alert>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">
                        For each SKU field choose <strong>Take</strong> (use
                        working value) or <strong>Keep</strong> (retain live
                        value).
                      </span>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            if (!compare) return;
                            const next: Record<string, TSyncFieldChoice> = {};
                            for (const f of compare.sku.fields) {
                              next[f.key] = "take";
                            }
                            setSelections((prev) => ({ ...prev, ...next }));
                          }}
                        >
                          Take all
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            if (!compare) return;
                            const next: Record<string, TSyncFieldChoice> = {};
                            for (const f of compare.sku.fields) {
                              next[f.key] = "keep";
                            }
                            setSelections((prev) => ({ ...prev, ...next }));
                          }}
                        >
                          Keep all
                        </Button>
                      </div>
                    </div>
                    <Table size="sm" bordered className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "22%" }}>Field</th>
                          <th style={{ width: "29%" }}>Live (SQL)</th>
                          <th style={{ width: "29%" }}>Working (staging)</th>
                          <th style={{ width: "20%" }}>Choice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compare.sku.fields.map((f) => (
                          <tr
                            key={f.key}
                            className={f.differs ? "table-warning" : undefined}
                          >
                            <td className="small">{f.label}</td>
                            <td
                              className="small text-muted"
                              style={{ wordBreak: "break-all" }}
                            >
                              {formatValue(f.sqlValue)}
                            </td>
                            <td
                              className="small"
                              style={{ wordBreak: "break-all" }}
                            >
                              {formatValue(f.mongoValue)}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Form.Check
                                  type="radio"
                                  id={`${f.key}-take`}
                                  label="Take"
                                  name={f.key}
                                  checked={selections[f.key] !== "keep"}
                                  disabled={!f.differs}
                                  onChange={() => setChoice(f.key, "take")}
                                />
                                <Form.Check
                                  type="radio"
                                  id={`${f.key}-keep`}
                                  label="Keep"
                                  name={f.key}
                                  checked={selections[f.key] === "keep"}
                                  disabled={!f.differs}
                                  onChange={() => setChoice(f.key, "keep")}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          disabled={loading || submitting || !compare || nothingToDo}
          onClick={handleSubmit}
        >
          {submitting ? <Spinner size="sm" animation="border" /> : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Live Templates tab ─────────────────────────────────────────────────────

/**
 * Detail modal for a LIVE template — shows the raw SQL data with no
 * transformation: auto-coupon selectors + timings (grouped together) and the
 * full SKU TemplateData JSON plus its timestamps.
 */
const LiveTemplateModal: React.FC<{
  template: IAutoCouponLiveTemplate | null;
  onClose: () => void;
}> = ({ template, onClose }) => (
  <Modal show={!!template} onHide={onClose} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Live template — {template?.domain}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {template && (
        <>
          <h6 className="mb-1">Auto-coupon template</h6>
          <div className="small text-muted mb-2">
            Version {template.version ?? "—"} · {template.templateView} ·{" "}
            {template.isActive ? "Active" : "Inactive"} · priority{" "}
            {template.priority}
          </div>

          <div className="fw-semibold small mb-1">Selectors</div>
          <pre
            className="bg-light p-3 rounded"
            style={{ fontSize: 12, maxHeight: 260, overflow: "auto" }}
          >
            {JSON.stringify(template.selectors, null, 2)}
          </pre>

          <div className="fw-semibold small mb-1 mt-2">Timings</div>
          <pre
            className="bg-light p-3 rounded"
            style={{ fontSize: 12, maxHeight: 220, overflow: "auto" }}
          >
            {JSON.stringify(template.timings, null, 2)}
          </pre>

          <div className="fw-semibold small mb-1 mt-2">Checkout URL regex</div>
          {template.checkoutPageUrlRegex ? (
            <code
              className="d-block bg-light p-2 rounded small"
              style={{ wordBreak: "break-all" }}
            >
              {template.checkoutPageUrlRegex}
            </code>
          ) : (
            <p className="text-muted small">Not set.</p>
          )}

          <hr />

          <h6 className="mb-1">SKU template</h6>
          {template.skuTemplateId ? (
            <>
              <div className="small text-muted mb-2">
                Version {template.skuVersion ?? "—"} ·{" "}
                {template.skuIsActive ? "Active" : "Inactive"}
                {template.skuCreatedAt && (
                  <>
                    {" "}
                    · created {new Date(template.skuCreatedAt).toLocaleString()}
                  </>
                )}
                {template.skuUpdatedAt && (
                  <>
                    {" "}
                    · updated {new Date(template.skuUpdatedAt).toLocaleString()}
                  </>
                )}
              </div>
              <div className="fw-semibold small mb-1">TemplateData</div>
              {template.skuTemplateData ? (
                <pre
                  className="bg-light p-3 rounded"
                  style={{ fontSize: 12, maxHeight: 260, overflow: "auto" }}
                >
                  {JSON.stringify(template.skuTemplateData, null, 2)}
                </pre>
              ) : (
                <p className="text-muted small">
                  TemplateData empty or unparseable.
                </p>
              )}
            </>
          ) : (
            <p className="text-muted small">No SKU template for this domain.</p>
          )}
        </>
      )}
    </Modal.Body>
  </Modal>
);

const LiveTemplatesTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState("");
  const [viewFilter, setViewFilter] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("");
  const [searchedDomain, setSearchedDomain] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<IAutoCouponLiveTemplate | null>(
    null
  );

  const queryParams = new URLSearchParams({ page: String(page), limit: "25" });
  if (searchedDomain) queryParams.set("domain", searchedDomain);
  if (viewFilter) queryParams.set("view", viewFilter);
  if (activeFilter !== "") queryParams.set("isActive", activeFilter);

  // Search-only: don't hit the live SQL DB until a domain has been searched.
  const hasSearched = searchedDomain.trim().length > 0;
  const { data, loading, error, refetch } =
    useGet<IAutoCouponLiveTemplateListResponse>(
      hasSearched ? `/auto-coupons/live-templates?${queryParams}` : null
    );

  const templates = data?.templates ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  const runSearch = () => {
    setPage(1);
    setSearchedDomain(domainFilter.trim());
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fmtSavings = (amount: number | null, currency: string | null) => {
    if (amount == null) return "—";
    return `${amount.toFixed(2)}${currency ? ` ${currency}` : ""}`;
  };

  const statusBadge = (status: TAutoCouponToolTestStatus | null) => {
    if (status === "pass") return <Badge bg="success">Pass</Badge>;
    if (status === "fail") return <Badge bg="danger">Fail</Badge>;
    return <Badge bg="secondary">Untested</Badge>;
  };

  const viewLive = (tpl: IAutoCouponLiveTemplate) => setSelected(tpl);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small">Domain</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search domain…"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runSearch();
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">View</Form.Label>
              <Form.Select
                size="sm"
                value={viewFilter}
                onChange={(e) => {
                  setViewFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="mobile">Mobile</option>
                <option value="both">Both</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Active</Form.Label>
              <Form.Select
                size="sm"
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex align-items-end gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={runSearch}
                disabled={!domainFilter.trim()}
              >
                <Search size={14} className="me-1" /> Search
              </Button>
              {hasSearched && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => refetch()}
                  title="Refresh results"
                >
                  <RefreshCw size={14} />
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && <Alert variant="danger">Failed to load live templates.</Alert>}

      {!loading && !error && !hasSearched && (
        <Alert variant="light" className="text-center text-muted border py-4">
          <Search size={18} className="me-2" />
          Search for a domain to load live templates from SQL.
        </Alert>
      )}

      {!loading && !error && hasSearched && (
        <>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th>Domain</th>
                <th>View</th>
                <th>Auto template</th>
                <th>SKU template</th>
                <th>Last tool test</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No live templates found.
                  </td>
                </tr>
              )}
              {templates.map((tpl) => {
                const isOpen = expanded.has(tpl.templateId);
                return (
                  <React.Fragment key={tpl.templateId}>
                    <tr>
                      <td>
                        {tpl.toolTests.length > 0 && (
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            onClick={() => toggleExpand(tpl.templateId)}
                          >
                            {isOpen ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </Button>
                        )}
                      </td>
                      <td className="small fw-semibold">{tpl.domain}</td>
                      <td className="small">
                        <Badge bg="info">{tpl.templateView}</Badge>
                      </td>
                      <td className="small">
                        <div className="d-flex align-items-center gap-1">
                          {tpl.version != null ? (
                            <Badge bg="dark">v{tpl.version}</Badge>
                          ) : (
                            <Badge bg="dark">v?</Badge>
                          )}
                          {tpl.isActive ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </div>
                      </td>
                      <td className="small">
                        {tpl.skuTemplateId ? (
                          <div className="d-flex align-items-center gap-1">
                            <Badge bg="warning" text="dark">
                              {tpl.skuVersion ?? "SKU"}
                            </Badge>
                            {tpl.skuIsActive ? (
                              <Badge bg="success">Active</Badge>
                            ) : (
                              <Badge bg="secondary">Inactive</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">None</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {tpl.lastToolTestedAt
                          ? new Date(tpl.lastToolTestedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>{statusBadge(tpl.lastStatus)}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          title="View selectors"
                          onClick={() => viewLive(tpl)}
                        >
                          <Search size={12} />
                        </Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td />
                        <td colSpan={7} className="bg-light">
                          <div className="small fw-semibold mb-2">
                            Tool tests by country
                          </div>
                          <Table size="sm" borderless className="mb-0">
                            <thead>
                              <tr className="text-muted small">
                                <th>Country</th>
                                <th>View</th>
                                <th>Status</th>
                                <th>Last tested</th>
                                <th>Coupons</th>
                                <th>Best savings</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tpl.toolTests.map((tt) => {
                                const latest = tt.history[0];
                                return (
                                  <tr key={`${tt.country}-${tt.templateView}`}>
                                    <td className="small">{tt.country}</td>
                                    <td>
                                      <Badge bg="info">{tt.templateView}</Badge>
                                    </td>
                                    <td>{statusBadge(tt.lastStatus)}</td>
                                    <td className="small text-muted">
                                      {tt.lastToolTestedAt
                                        ? new Date(
                                            tt.lastToolTestedAt
                                          ).toLocaleString()
                                        : "—"}
                                    </td>
                                    <td className="small">
                                      {latest
                                        ? `${latest.successfulCoupons}/${latest.couponsEvaluated}`
                                        : "—"}
                                    </td>
                                    <td className="small">
                                      {latest
                                        ? fmtSavings(
                                            latest.bestSavings,
                                            latest.currency
                                          )
                                        : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">{total} total</small>
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <LiveTemplateModal
        template={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

// ─── Sync Audit tab ──────────────────────────────────────────────────────────

const SyncAuditTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState("");
  const [searchedDomain, setSearchedDomain] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const {
    data,
    loading,
    error,
    execute: fetchAudit,
  } = useGetLazy<IAutoCouponSyncAuditListResponse>();

  const audits = data?.audits ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runSearch = () => {
    setPage(1);
    setSearchedDomain(domainFilter.trim());
    setHasSearched(true);
  };

  React.useEffect(() => {
    if (!hasSearched) return;
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: "25",
    });
    if (searchedDomain) queryParams.set("domain", searchedDomain);
    fetchAudit(`/auto-coupons/sync-audit?${queryParams}`);
  }, [hasSearched, page, searchedDomain, fetchAudit]);

  const refreshSearch = () => {
    if (!hasSearched) return;
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: "25",
    });
    if (searchedDomain) queryParams.set("domain", searchedDomain);
    fetchAudit(`/auto-coupons/sync-audit?${queryParams}`);
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small">Domain</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search domain…"
                value={domainFilter}
                onChange={(e) => {
                  setDomainFilter(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") runSearch();
                }}
              />
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="primary" size="sm" onClick={runSearch}>
                <Search size={14} className="me-1" /> Search
              </Button>
              <Button
                className="ms-2"
                variant="outline-primary"
                size="sm"
                onClick={refreshSearch}
                disabled={!hasSearched}
              >
                <RefreshCw size={14} className="me-1" /> Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && <Alert variant="danger">Failed to load sync audit log.</Alert>}

      {!loading && !error && !hasSearched && (
        <Alert variant="light" className="text-center text-muted border py-4">
          <Search size={18} className="me-2" />
          Search to load sync audit entries.
        </Alert>
      )}

      {!loading && !error && hasSearched && (
        <>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th>Date</th>
                <th>Domain</th>
                <th>View</th>
                <th>User</th>
                <th>Changes</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No sync audit entries found.
                  </td>
                </tr>
              )}
              {audits.map((audit) => {
                const isOpen = expanded.has(audit._id);
                return (
                  <React.Fragment key={audit._id}>
                    <tr>
                      <td>
                        {audit.changes.length > 0 && (
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            onClick={() => toggleExpand(audit._id)}
                          >
                            {isOpen ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </Button>
                        )}
                      </td>
                      <td className="small text-muted">
                        {new Date(audit.syncedAt).toLocaleString()}
                      </td>
                      <td className="small fw-semibold">{audit.domain}</td>
                      <td className="small">
                        <Badge bg="info">{audit.templateView}</Badge>
                      </td>
                      <td className="small">{audit.userEmail ?? "—"}</td>
                      <td className="small">
                        <Badge bg="secondary">{audit.changes.length}</Badge>
                      </td>
                      <td>
                        {audit.success ? (
                          <Badge bg="success">Success</Badge>
                        ) : (
                          <Badge bg="danger">Failed</Badge>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td />
                        <td colSpan={6} className="bg-light">
                          {!audit.success && audit.message && (
                            <div className="text-danger small mb-2">
                              {audit.message}
                            </div>
                          )}
                          <div className="small mb-2">
                            <span className="text-muted">
                              A live template combines two SQL templates:
                            </span>
                            <div className="d-flex flex-wrap gap-3 mt-1">
                              <span>
                                <Badge bg="primary" className="me-1">
                                  Auto
                                </Badge>
                                <code>{audit.autoTemplateId ?? "—"}</code>
                              </span>
                              <span>
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="me-1"
                                >
                                  SKU
                                </Badge>
                                <code>{audit.skuTemplateId ?? "—"}</code>
                              </span>
                            </div>
                          </div>
                          <Table size="sm" borderless className="mb-0">
                            <thead>
                              <tr className="text-muted small">
                                <th>Field</th>
                                <th>Choice</th>
                                <th>Mongo value</th>
                                <th>SQL value</th>
                                <th>Applied value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {audit.changes.map((change) => (
                                <tr key={change.key}>
                                  <td className="small">{change.label}</td>
                                  <td className="small">
                                    <Badge
                                      bg={
                                        change.choice === "take"
                                          ? "primary"
                                          : "secondary"
                                      }
                                    >
                                      {change.choice}
                                    </Badge>
                                  </td>
                                  <td className="small text-muted">
                                    {formatValue(change.mongoValue)}
                                  </td>
                                  <td className="small text-muted">
                                    {formatValue(change.sqlValue)}
                                  </td>
                                  <td className="small">
                                    {formatValue(change.appliedValue)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">{total} total</small>
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

// ─── Bulk put-live modal ─────────────────────────────────────────────────────

const BulkPutLiveModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onDone: () => void;
}> = ({ show, onClose, onDone }) => {
  const {
    data: preview,
    loading: previewLoading,
    error: previewError,
    execute: fetchPreview,
  } = useGetLazy<IAutoCouponUnsyncedListResponse>();
  const { execute: runBulk } = usePost<IAutoCouponBulkPutLiveResponse>();

  const [mode, setMode] = useState<TAutoCouponPutLiveMode>("both-absent");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IAutoCouponBulkPutLiveResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Reset to the default mode each time the modal opens.
  React.useEffect(() => {
    if (show) setMode("both-absent");
  }, [show]);

  // (Re)load the preview whenever the modal opens or the mode changes.
  React.useEffect(() => {
    if (!show) return;
    setResult(null);
    setError(null);
    fetchPreview(
      `/auto-coupons/templates/unsynced?page=1&limit=${AUTO_COUPON_BULK_PUT_LIVE_MAX}&mode=${mode}`
    );
  }, [show, mode, fetchPreview]);

  const items = preview?.templates ?? [];
  const total = preview?.total ?? 0;
  const truncated = total > items.length;

  const handleRun = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await runBulk("/auto-coupons/templates/put-live-unsynced", {
        templateIds: items.map((t) => t._id),
        mode,
      });
      setResult(res);
      onDone();
    } catch (e: any) {
      setError(e?.data?.message ?? e?.message ?? "Bulk put-live failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const headers = ["domain", "autoLive", "skuLive"];
    const lines = [
      headers.join(","),
      ...result.results.map((r) =>
        [r.domain, r.autoLive, r.skuLive].map(csvEscape).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `put-live-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const checkOrDash = (on: boolean) =>
    on ? (
      <CheckCircle size={13} className="text-success" />
    ) : (
      <span className="text-muted">—</span>
    );

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Put unsynced items live</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Pushes live SQL records for valid staging templates that are missing
          from production. One record per domain (mobile view preferred).
        </p>

        <div className="mb-3">
          <div className="fw-semibold small mb-2">Select what to push:</div>
          <Form.Check
            type="radio"
            id="put-live-mode-both"
            name="put-live-mode"
            className="small mb-1"
            label={
              <span>
                <strong>Mode 1 — Both Auto + SKU are new</strong>
                <span className="text-muted ms-1">
                  Only push when neither the Auto template nor the SKU template
                  exists in SQL yet. Skips any domain where even one half is
                  already live.
                </span>
              </span>
            }
            checked={mode === "both-absent"}
            onChange={() => setMode("both-absent")}
          />
          <Form.Check
            type="radio"
            id="put-live-mode-asis"
            name="put-live-mode"
            className="small mb-1"
            label={
              <span>
                <strong>Mode 2 — Push whichever half is new</strong>
                <span className="text-muted ms-1">
                  Push the Auto or SKU half independently, but only if that half
                  does not exist in SQL yet. Will not touch halves that already
                  have a live record.
                </span>
              </span>
            }
            checked={mode === "one-absent"}
            onChange={() => setMode("one-absent")}
          />
          <Form.Check
            type="radio"
            id="put-live-mode-updates"
            name="put-live-mode"
            className="small mb-1"
            label={
              <span>
                <strong>
                  Mode 3 — Push all outstanding (includes updates)
                </strong>
                <span className="text-muted ms-1">
                  Push new templates AND overwrite existing live records with
                  staging changes that have never been synced (SQL diff items).
                </span>
              </span>
            }
            checked={mode === "all-updates"}
            onChange={() => setMode("all-updates")}
          />
        </div>

        {previewLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {previewError && (
          <Alert variant="danger" className="small py-2">
            Failed to load preview.
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="small py-2">
            {error}
          </Alert>
        )}

        {!previewLoading && !result && preview && (
          <>
            <div className="small mb-2">
              <strong>{items.length}</strong> item
              {items.length === 1 ? "" : "s"} ready to put live
            </div>
            {truncated && (
              <Alert variant="warning" className="small py-2">
                {total} items are unsynced — only the first {items.length} will
                be processed in this batch. Narrow by domain on the list to
                handle the rest.
              </Alert>
            )}
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              <Table size="sm" hover className="mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Domain</th>
                    <th>View</th>
                    <th>Auto</th>
                    <th>SKU</th>
                    <th>Will push</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        Nothing to put live — every item already has a live
                        template.
                      </td>
                    </tr>
                  ) : (
                    items.map((t) => (
                      <tr key={t._id}>
                        <td>{t.domain}</td>
                        <td>
                          <Badge bg="secondary">{t.templateView}</Badge>
                        </td>
                        <td>{t.version != null ? `v${t.version}` : "—"}</td>
                        <td>
                          {t.hasWorkingSku
                            ? t.workingSkuVersion != null
                              ? t.workingSkuVersion
                              : "true"
                            : "—"}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {t.needsAuto && t.pushAuto && (
                              <Badge bg="success">Auto (new)</Badge>
                            )}
                            {!t.needsAuto && t.pushAuto && (
                              <Badge bg="info">Auto (update)</Badge>
                            )}
                            {t.needsSku && t.pushSku && (
                              <Badge bg="success">SKU (new)</Badge>
                            )}
                            {!t.needsSku && t.pushSku && (
                              <Badge bg="info">SKU (update)</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {result && (
          <>
            <Alert variant="success" className="small py-2">
              <CheckCircle size={12} className="me-1" />
              Processed <strong>{result.totalProcessed}</strong> —{" "}
              {result.autoLiveCount} auto template
              {result.autoLiveCount === 1 ? "" : "s"} and {result.skuLiveCount}{" "}
              SKU template{result.skuLiveCount === 1 ? "" : "s"} put live.
            </Alert>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <Table size="sm" hover className="mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Domain</th>
                    <th>Auto live</th>
                    <th>SKU live</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={`${r.domain}-${i}`}>
                      <td>{r.domain}</td>
                      <td>{checkOrDash(r.autoLive)}</td>
                      <td>{checkOrDash(r.skuLive)}</td>
                      <td
                        className={
                          r.success ? "text-muted small" : "text-danger small"
                        }
                        title={r.message}
                      >
                        {r.success ? "ok" : "failed"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>
        {result ? (
          <Button size="sm" variant="outline-success" onClick={downloadCsv}>
            <Download size={13} className="me-1" /> Download CSV
          </Button>
        ) : (
          <Button
            size="sm"
            variant="success"
            onClick={handleRun}
            disabled={submitting || items.length === 0}
          >
            {submitting ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <Upload size={13} className="me-1" />
            )}
            Put {items.length > 0 ? items.length : ""} live
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ─── Export put-live CSV by date/time ────────────────────────────────────────

/** yyyy-MM-ddTHH:mm in local time, for a <input type="datetime-local">. */
const toLocalDateTimeInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ExportPutLiveModal: React.FC<{
  show: boolean;
  onClose: () => void;
}> = ({ show, onClose }) => {
  const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const [from, setFrom] = useState(toLocalDateTimeInput(startOfToday()));
  const [to, setTo] = useState(toLocalDateTimeInput(new Date()));
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { execute: fetchCsv } = useGetLazy<string>();

  React.useEffect(() => {
    if (!show) return;
    setFrom(toLocalDateTimeInput(startOfToday()));
    setTo(toLocalDateTimeInput(new Date()));
    setError(null);
  }, [show]);

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", new Date(from).toISOString());
      if (to) params.set("to", new Date(to).toISOString());
      const csv = await fetchCsv(
        `/auto-coupons/templates/put-live-export?${params.toString()}`,
        { responseType: "text" }
      );
      if (csv == null) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `put-live-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.data?.message ?? e?.message ?? "Export failed.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Export put-live CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Rebuilds the "what was taken live" CSV from the sync log for a time
          range — use this to re-download a past run after the modal was closed.
          Columns: domain, autoLive, skuLive, syncedAt, syncedBy.
        </p>
        <Row className="g-2">
          <Col md={6}>
            <Form.Label className="small mb-0">From</Form.Label>
            <Form.Control
              size="sm"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small mb-0">To</Form.Label>
            <Form.Control
              size="sm"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Col>
        </Row>
        {error && (
          <Alert variant="danger" className="small py-2 mt-2 mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          size="sm"
          variant="outline-success"
          onClick={download}
          disabled={downloading}
        >
          {downloading ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : (
            <Download size={13} className="me-1" />
          )}
          Download CSV
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const AutoCouponTemplates: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"staging" | "live" | "audit">(
    "staging"
  );
  const [pushLiveId, setPushLiveId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState("");
  const [invalidFilter, setInvalidFilter] = useState<string>("");
  // "unsynced" (default) = items that need action (missing live rows, SQL diff
  // detected, or staging updated since last sync); "all" = the full staging list.
  const [liveStatusFilter, setLiveStatusFilter] = useState<"unsynced" | "all">(
    "unsynced"
  );
  const [searchedDomain, setSearchedDomain] = useState("");
  const [searchedInvalid, setSearchedInvalid] = useState<string>("");
  const [searchedLiveStatus, setSearchedLiveStatus] = useState<
    "unsynced" | "all"
  >("unsynced");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<IAutoCouponTemplate | null>(null);
  const [showLoadSql, setShowLoadSql] = useState(false);
  const [showBulkPutLive, setShowBulkPutLive] = useState(false);
  const [showExportPutLive, setShowExportPutLive] = useState(false);
  const [sqlDiffChecking, setSqlDiffChecking] = useState(false);
  const [sqlDiffResult, setSqlDiffResult] =
    useState<IAutoCouponSqlDiffCheckResponse | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncResult, setSyncResult] =
    useState<IAutoCouponSyncSqlResponse | null>(null);

  const {
    data: allData,
    loading: allLoading,
    error: allError,
    execute: fetchTemplates,
  } = useGetLazy<IAutoCouponTemplateListResponse>();

  const {
    data: unsyncedData,
    loading: unsyncedLoading,
    error: unsyncedError,
    execute: fetchUnsynced,
  } = useGetLazy<IAutoCouponUnsyncedListResponse>();

  const { execute: invalidate } = usePatch<IAutoCouponTemplate>();
  const { execute: syncToSql } = usePost<IAutoCouponSyncSqlResponse>();
  const { execute: runSqlDiffCheck } =
    usePost<IAutoCouponSqlDiffCheckResponse>();

  const isUnsyncedView = searchedLiveStatus === "unsynced";

  const refreshTemplates = React.useCallback(() => {
    if (!hasSearched) return;
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: "25",
    });
    if (searchedDomain) queryParams.set("domain", searchedDomain);
    if (searchedLiveStatus === "unsynced") {
      // Show every unsynced item in the list regardless of SKU presence; the
      // bulk modal is where the push mode choice is made.
      queryParams.set("mode", "one-absent");
      fetchUnsynced(`/auto-coupons/templates/unsynced?${queryParams}`);
    } else {
      if (searchedInvalid !== "") queryParams.set("invalid", searchedInvalid);
      fetchTemplates(`/auto-coupons/templates?${queryParams}`);
    }
  }, [
    hasSearched,
    page,
    searchedDomain,
    searchedInvalid,
    searchedLiveStatus,
    fetchTemplates,
    fetchUnsynced,
  ]);

  // Unsynced is the default view, so load it on mount without a search click.
  React.useEffect(() => {
    if (!hasSearched && liveStatusFilter === "unsynced") {
      setSearchedLiveStatus("unsynced");
      setHasSearched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const handleInvalidate = async (id: string) => {
    if (!window.confirm("Mark this template as invalid?")) return;
    try {
      await invalidate(`/auto-coupons/templates/${id}/invalidate`);
      refreshTemplates();
    } catch {
      // handled by hook
    }
  };

  const handleSyncSql = async (id: string) => {
    setSyncingId(id);
    setSyncResult(null);
    try {
      const result = await syncToSql(
        `/auto-coupons/templates/${id}/sync-sql`,
        {}
      );
      setSyncResult(result);
      refreshTemplates();
    } catch (err: any) {
      setSyncResult({
        success: false,
        message: err?.data?.message ?? "Sync failed",
      });
    } finally {
      setSyncingId(null);
    }
  };

  const handleCheckSqlDiff = async () => {
    setSqlDiffChecking(true);
    setSqlDiffResult(null);
    try {
      const result = await runSqlDiffCheck(
        "/auto-coupons/templates/check-sql-diff",
        {}
      );
      setSqlDiffResult(result);
      refreshTemplates();
    } catch {
      // handled via alert if needed
    } finally {
      setSqlDiffChecking(false);
    }
  };

  const runSearch = () => {
    setPage(1);
    setSearchedDomain(domainFilter.trim());
    setSearchedInvalid(invalidFilter);
    setSearchedLiveStatus(liveStatusFilter);
    setHasSearched(true);
  };

  const templates: IAutoCouponUnsyncedTemplate[] = isUnsyncedView
    ? (unsyncedData?.templates ?? [])
    : ((allData?.templates ?? []) as IAutoCouponUnsyncedTemplate[]);
  const total = isUnsyncedView
    ? (unsyncedData?.total ?? 0)
    : (allData?.total ?? 0);
  const loading = isUnsyncedView ? unsyncedLoading : allLoading;
  const error = isUnsyncedView ? unsyncedError : allError;
  const totalPages = Math.ceil(total / 25);

  const renderAutoCell = (tpl: IAutoCouponTemplate) => {
    const working = tpl.version != null ? `v${tpl.version}` : "—";
    return (
      <Badge bg={tpl.version != null ? "success" : "secondary"}>
        {working}
      </Badge>
    );
  };

  const renderSkuCell = (tpl: IAutoCouponTemplate) => {
    const working =
      tpl.workingSkuVersion != null
        ? tpl.workingSkuVersion
        : tpl.hasWorkingSku
          ? "true"
          : "—";
    return (
      <Badge bg={tpl.hasWorkingSku ? "success" : "secondary"}>{working}</Badge>
    );
  };

  const renderSyncOutcomeBadge = (label: "Auto" | "SKU", outcome: string) => {
    const variant =
      outcome === "created" || outcome === "updated"
        ? "success"
        : outcome === "failed"
          ? "danger"
          : outcome === "none"
            ? "secondary"
            : "light";
    const text =
      outcome === "created" || outcome === "updated"
        ? outcome
        : outcome === "failed"
          ? "failed"
          : outcome === "none"
            ? "n/a"
            : "skipped";
    return (
      <Badge
        bg={variant as any}
        text={variant === "light" ? "dark" : undefined}
      >
        {label} {text}
      </Badge>
    );
  };

  const renderSyncedCell = (tpl: IAutoCouponTemplate) => {
    if (!tpl.syncedToSqlAt && !tpl.sqlMismatch)
      return <span className="text-muted small">—</span>;

    const autoOutcome = tpl.lastSyncAutoOutcome ?? "none";
    const skuOutcome = tpl.lastSyncSkuOutcome ?? "none";
    const date = tpl.syncedToSqlAt
      ? new Date(tpl.syncedToSqlAt).toLocaleDateString()
      : null;
    return (
      <div className="d-flex flex-column gap-2">
        {date && <span className="small text-muted">{date}</span>}
        <div className="d-flex gap-1 flex-wrap">
          {tpl.syncedToSqlAt && renderSyncOutcomeBadge("Auto", autoOutcome)}
          {tpl.syncedToSqlAt && renderSyncOutcomeBadge("SKU", skuOutcome)}
          {tpl.sqlMismatch && (
            <Badge
              bg="warning"
              text="dark"
              title="SQL diff scan detected live values differ from staging"
            >
              SQL diff
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Auto Coupon Templates</h4>
        {activeTab === "staging" && (
          <Dropdown as={ButtonGroup} size="sm">
            <Button
              variant="success"
              onClick={() => setShowBulkPutLive(true)}
              title="Preview and bulk put live every item missing a live template"
            >
              <Upload size={14} className="me-1" /> Put unsynced live
              {isUnsyncedView && total > 0 && (
                <Badge bg="light" text="dark" className="ms-1">
                  {total}
                </Badge>
              )}
            </Button>
            <Dropdown.Toggle split variant="success" />
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={handleCheckSqlDiff}
                disabled={sqlDiffChecking}
              >
                <Search size={14} className="me-2" />
                {sqlDiffChecking
                  ? "Checking…"
                  : sqlDiffResult
                    ? `Check SQL diff (• ${sqlDiffResult.mismatches} mismatch${sqlDiffResult.mismatches === 1 ? "" : "es"})`
                    : "Check SQL diff"}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowExportPutLive(true)}>
                <Download size={14} className="me-2" /> Export put-live CSV
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowLoadSql(true)}>
                <Download size={14} className="me-2" /> Load from SQL
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={refreshTemplates} disabled={!hasSearched}>
                <RefreshCw size={14} className="me-2" /> Refresh
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      {/* Stats cards */}
      <TemplateStatsCards />

      {/* Tab switcher */}
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(k) =>
          setActiveTab((k ?? "staging") as "staging" | "live" | "audit")
        }
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="staging">
            Staging Templates
            {hasSearched && (isUnsyncedView ? unsyncedData : allData) && (
              <Badge bg="secondary" className="ms-2">
                {total}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="live">Live Templates</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="audit">Sync Audit Log</Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === "live" && <LiveTemplatesTab />}

      {activeTab === "audit" && <SyncAuditTab />}

      {activeTab === "staging" && (
        <>
          {syncResult && (
            <Alert
              variant={syncResult.success ? "success" : "danger"}
              dismissible
              onClose={() => setSyncResult(null)}
            >
              {syncResult.message}
            </Alert>
          )}

          {/* Filters */}
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <Form.Label className="small">Domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Search domain…"
                    value={domainFilter}
                    onChange={(e) => {
                      setDomainFilter(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") runSearch();
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Live status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={liveStatusFilter}
                    onChange={(e) =>
                      setLiveStatusFilter(e.target.value as "unsynced" | "all")
                    }
                  >
                    <option value="unsynced">Outstanding (needs action)</option>
                    <option value="all">All</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={invalidFilter}
                    disabled={liveStatusFilter === "unsynced"}
                    title={
                      liveStatusFilter === "unsynced"
                        ? "Unsynced only lists valid templates"
                        : undefined
                    }
                    onChange={(e) => setInvalidFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="false">Valid</option>
                    <option value="true">Invalid</option>
                  </Form.Select>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" size="sm" onClick={runSearch}>
                    <Search size={14} className="me-1" /> Search
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {error && <Alert variant="danger">Failed to load templates.</Alert>}

          {!loading && !error && !hasSearched && (
            <Alert
              variant="light"
              className="text-center text-muted border py-4"
            >
              <Search size={18} className="me-2" />
              Search to load staging templates.
            </Alert>
          )}

          {!loading && !error && hasSearched && (
            <>
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Mode</th>
                    <th>Auto</th>
                    <th>SKU</th>
                    {isUnsyncedView && <th>Needs live</th>}
                    <th>Created by</th>
                    <th>Synced to SQL</th>
                    <th>Last run</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 && (
                    <tr>
                      <td
                        colSpan={isUnsyncedView ? 9 : 8}
                        className="text-center text-muted py-4"
                      >
                        No templates found.
                      </td>
                    </tr>
                  )}
                  {templates.map((tpl) => (
                    <tr key={tpl._id}>
                      <td>
                        <span className="d-inline-flex align-items-center gap-2">
                          <TemplateValidIcon invalid={tpl.invalid} />
                          <TemplateViewIcon view={tpl.templateView} />
                          {tpl.domain}
                        </span>
                      </td>
                      <td>
                        <Badge bg="info" text="dark">
                          {tpl.templateMode}
                        </Badge>
                      </td>
                      <td>{renderAutoCell(tpl)}</td>
                      <td>{renderSkuCell(tpl)}</td>
                      {isUnsyncedView && (
                        <td>
                          <div className="d-flex gap-1 flex-wrap align-items-center">
                            {tpl.needsAuto && (
                              <Badge bg="warning" text="dark">
                                Auto
                              </Badge>
                            )}
                            {tpl.needsSku && (
                              <Badge bg="warning" text="dark">
                                SKU
                              </Badge>
                            )}
                            {!tpl.needsAuto && !tpl.needsSku && (
                              <span className="text-muted small">—</span>
                            )}
                            {!tpl.bothValid &&
                              (tpl.hasWorkingSku ? (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  title="No valid auto template on the dashboard — excluded by the default 'both valid' bulk mode"
                                >
                                  sku only
                                </Badge>
                              ) : (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  title="No valid SKU template on the dashboard — excluded by the default 'both valid' bulk mode"
                                >
                                  auto only
                                </Badge>
                              ))}
                          </div>
                        </td>
                      )}
                      <td
                        className="small text-muted"
                        style={{
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={tpl.recentUserEmail ?? undefined}
                      >
                        {tpl.recentUserEmail ?? "—"}
                      </td>
                      <td>{renderSyncedCell(tpl)}</td>
                      <td className="small text-muted" style={{ whiteSpace: "nowrap" }}>
                        {tpl.lastRunAt ? (
                          <span title={new Date(tpl.lastRunAt).toLocaleString()}>
                            {timeAgo(tpl.lastRunAt)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            title="View selectors"
                            onClick={() => setSelectedTemplate(tpl)}
                          >
                            <Search size={12} />
                          </Button>
                          {!tpl.invalid && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              title="Push live (compare & sync to SQL)"
                              onClick={() => setPushLiveId(tpl._id)}
                            >
                              <Upload size={12} />
                            </Button>
                          )}
                          {!tpl.invalid && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              title="Mark invalid"
                              onClick={() => handleInvalidate(tpl._id)}
                            >
                              <XCircle size={12} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">{total} total</small>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Prev
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <SelectorsModal
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
      />
      <LoadSqlModal
        show={showLoadSql}
        onClose={() => setShowLoadSql(false)}
        onLoaded={(tpl) => {
          setShowLoadSql(false);
          setSelectedTemplate(tpl);
        }}
      />
      <PushLiveModal
        templateId={pushLiveId}
        onClose={() => setPushLiveId(null)}
        onDone={() => {
          setSyncResult(null);
          setPushLiveId(null);
          refreshTemplates();
        }}
      />
      <BulkPutLiveModal
        show={showBulkPutLive}
        onClose={() => setShowBulkPutLive(false)}
        onDone={refreshTemplates}
      />
      <ExportPutLiveModal
        show={showExportPutLive}
        onClose={() => setShowExportPutLive(false)}
      />
    </div>
  );
};
